import { useEffect } from 'react';
import { HelpCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * One shortcut entry — keys on the left, a short label on the right.
 * `keys` may be a single key, a combination like "Cmd+Enter", or the chained
 * form "D · E · T · N" used by the USI-007 triage modal to indicate a set
 * of single-key options.
 */
export interface KeyboardShortcut {
  keys: string;
  label: string;
}

interface KeyboardHintBarProps {
  visible: boolean;
  onToggle: (visible: boolean) => void;
  /**
   * USI-007: the hint set this bar renders. List-view callers pass
   * `DEFAULT_LIST_SHORTCUTS`; the triage modal passes its own set so the
   * keys advertised match the keymap the modal actually wires.
   *
   * Required (no default) so the modal can't silently render list-view
   * shortcuts by forgetting to pass its own.
   */
  shortcuts: ReadonlyArray<KeyboardShortcut>;
}

const FADE_TIMEOUT_MS = 8000;

/**
 * USI-007: the default list-view shortcut set, exported so the original
 * callers can keep their rendered output byte-identical when they wire
 * the new `shortcuts` prop. New surfaces (USI-007 TriageModal, etc.) build
 * their own arrays.
 */
export const DEFAULT_LIST_SHORTCUTS: ReadonlyArray<KeyboardShortcut> = [
  { keys: 'J/K', label: 'Navigate' },
  { keys: '↑/↓', label: 'Navigate' },
  { keys: 'Enter', label: 'Open' },
  { keys: 'Esc', label: 'Close' },
  { keys: 'C', label: 'Contribution' },
  { keys: 'E', label: 'Expenditure' },
  { keys: 'D', label: 'Dismiss' },
  { keys: 'X', label: 'Check' },
];

export function KeyboardHintBar({ visible, onToggle, shortcuts }: KeyboardHintBarProps) {
  useEffect(() => {
    if (!visible) return;
    const timer = setTimeout(() => onToggle(false), FADE_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [visible, onToggle]);

  return (
    <div className="fixed bottom-4 right-4 z-50 flex items-center gap-2">
      {visible && (
        <div
          data-testid="keyboard-hint-bar"
          className="flex items-center gap-3 rounded-lg border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-md animate-in fade-in slide-in-from-bottom-2"
        >
          {shortcuts.map(({ keys, label }) => (
            <span key={keys} className="flex items-center gap-1">
              <kbd className="rounded border bg-muted px-1.5 py-0.5 font-mono text-[10px]">{keys}</kbd>
              <span className="text-muted-foreground">{label}</span>
            </span>
          ))}
        </div>
      )}
      <button
        type="button"
        onClick={() => onToggle(!visible)}
        className={cn(
          'rounded-full border p-1.5 text-muted-foreground shadow-sm transition-colors hover:bg-accent',
          !visible && 'bg-popover',
        )}
        aria-label="Keyboard shortcuts"
      >
        <HelpCircle className="h-4 w-4" />
      </button>
    </div>
  );
}
